import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const ACHIEVEMENTS = [
  { id: 'first-game', label: 'First Game', icon: 'Gamepad2', desc: 'Created your first game', color: '#F59E0B', unlocked: true },
  { id: 'first-video', label: 'First Video', icon: 'Video', desc: 'Generated your first video', color: '#10B981', unlocked: true },
  { id: 'first-remix', label: 'First Remix', icon: 'Shuffle', desc: 'Remixed a creation', color: 'var(--color-primary)', unlocked: false },
  { id: 'first-100-plays', label: '100 Plays', icon: 'Play', desc: 'Reached 100 total plays', color: 'var(--color-secondary)', unlocked: true },
  { id: 'publisher', label: 'Publisher', icon: 'Globe', desc: 'Published 3+ creations', color: '#F43F5E', unlocked: false },
  { id: 'creator-5', label: 'Prolific Creator', icon: 'Star', desc: 'Created 5+ projects', color: '#EC4899', unlocked: false },
];

const CREATOR_LEVELS = [
  { level: 1, label: 'Beginner', minXP: 0, color: '#9CA3AF' },
  { level: 2, label: 'Creator', minXP: 100, color: '#10B981' },
  { level: 3, label: 'Builder', minXP: 300, color: 'var(--color-primary)' },
  { level: 4, label: 'Innovator', minXP: 600, color: 'var(--color-secondary)' },
  { level: 5, label: 'Master', minXP: 1000, color: '#F59E0B' },
];

export default function CreatorAchievements({ games }) {
  const [isOpen, setIsOpen] = useState(false);

  const totalXP = (games?.length || 0) * 50 + (games?.filter(g => g?.published)?.length || 0) * 30;
  const currentLevel = CREATOR_LEVELS?.reduce((acc, lvl) => totalXP >= lvl?.minXP ? lvl : acc, CREATOR_LEVELS?.[0]);
  const nextLevel = CREATOR_LEVELS?.find(l => l?.level === currentLevel?.level + 1);
  const xpProgress = nextLevel ? ((totalXP - currentLevel?.minXP) / (nextLevel?.minXP - currentLevel?.minXP)) * 100 : 100;
  const unlockedCount = ACHIEVEMENTS?.filter(a => a?.unlocked)?.length;

  return (
    <div className="rounded-2xl overflow-hidden" style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', boxShadow: 'var(--shadow-sm)' }}>
      <button
        onClick={() => setIsOpen(v => !v)}
        className="w-full flex items-center justify-between px-4 py-3 transition-all hover:opacity-90"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${currentLevel?.color}, ${currentLevel?.color}aa)` }}>
            <Icon name="Trophy" size={16} color="white" />
          </div>
          <div className="text-left">
            <p className="text-xs font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>
              Level {currentLevel?.level} · {currentLevel?.label}
            </p>
            <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>
              {unlockedCount}/{ACHIEVEMENTS?.length} badges · {totalXP} XP
            </p>
          </div>
        </div>
        <Icon name={isOpen ? 'ChevronUp' : 'ChevronDown'} size={14} color="var(--color-muted-foreground)" />
      </button>

      {isOpen && (
        <div className="px-4 pb-4 flex flex-col gap-3 animate-fade-in-up">
          {/* XP Progress */}
          <div>
            <div className="flex justify-between text-xs mb-1" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-data)' }}>
              <span>{totalXP} XP</span>
              <span>{nextLevel ? `${nextLevel?.minXP} XP to Level ${nextLevel?.level}` : 'Max Level!'}</span>
            </div>
            <div className="h-2 rounded-full overflow-hidden" style={{ background: 'var(--color-muted)' }}>
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{ width: `${Math.min(xpProgress, 100)}%`, background: `linear-gradient(90deg, ${currentLevel?.color}, ${currentLevel?.color}cc)` }}
              />
            </div>
          </div>

          {/* Badges grid */}
          <div className="grid grid-cols-3 gap-2">
            {ACHIEVEMENTS?.map((achievement) => (
              <div
                key={achievement?.id}
                className="flex flex-col items-center gap-1 p-2 rounded-xl border text-center"
                style={{
                  borderColor: achievement?.unlocked ? `${achievement?.color}40` : 'var(--color-border)',
                  background: achievement?.unlocked ? `${achievement?.color}08` : 'var(--color-muted)',
                  opacity: achievement?.unlocked ? 1 : 0.5,
                }}
                title={achievement?.desc}
              >
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center"
                  style={{ background: achievement?.unlocked ? achievement?.color : 'var(--color-border)' }}
                >
                  <Icon name={achievement?.icon} size={14} color={achievement?.unlocked ? 'white' : 'var(--color-muted-foreground)'} />
                </div>
                <span className="text-xs font-semibold leading-tight" style={{ color: achievement?.unlocked ? 'var(--color-foreground)' : 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)', fontSize: 9 }}>
                  {achievement?.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
